<div class="ufp-widget-container">
  <!-- Error Message -->
  <div *ngIf="error" class="ufp-error-message">
    {{error}}
  </div>

  <ng-container *ngIf="isInitialized">
    <!-- Header Section -->
    <div class="ufp-user-header">
      <!-- Location & Filters -->
      <div class="ufp-header-controls">
        <!-- Location Selector -->
        <div class="ufp-location-selector">
          <span class="ufp-label" [style.color]="currentTheme.textColor">Location:</span>
          <ng-container *ngIf="!showTenantDropdown && tenants.length > 0">
            {{tenants[0].tenant_name}}
          </ng-container>
          <div *ngIf="showTenantDropdown" class="ufp-tenant-selector">
            <select #tenantSelect (change)="onTenantChange(tenantSelect.value)">
              <option *ngFor="let tenant of tenants" 
                      [value]="tenant.id"
                      [selected]="tenant.id === selectedTenantUuid">
                {{tenant.tenant_name}}
              </option>
            </select>
          </div>
        </div>

        <!-- Filter Controls -->
        <div class="ufp-filters-section">
          <span class="ufp-label" [style.color]="currentTheme.textColor">Filter by:</span>
          <select 
            class="ufp-filter-select"
            (change)="onTypeFilterChange($event)"
          >
            <option value="">All Class Types</option>
            <option 
              *ngFor="let type of classTypes" 
              [value]="type.id"
            >
              {{type.name}}
            </option>
          </select>

          <select 
            class="ufp-filter-select"
            (change)="onCoachFilterChange($event)"
          >
            <option value="">All Coaches</option>
            <option 
              *ngFor="let coach of coaches" 
              [value]="coach.id"
            >
              {{coach.name}}
            </option>
          </select>
        </div>
      </div>

      <!-- User Section -->
      <div class="ufp-user-section">
        <ng-container *ngIf="isLoggedIn$ | async; else loginButton">
          <!-- Logged In User Info -->
          <div class="ufp-user-info" (click)="toggleUserMenu($event)">
            <span class="ufp-user-name">{{userName}}</span>
            <div class="ufp-avatar" [class.has-image]="userAvatar" 
                 [style.backgroundImage]="userAvatar ? 'url(' + userAvatar + ')' : 'none'">
              <span *ngIf="!userAvatar">{{userInitial}}</span>
            </div>
          </div>
          <!-- User Menu Dropdown -->
          <div class="ufp-user-menu" *ngIf="showUserMenu">
            <div class="ufp-menu-item" (click)="logout($event)">
              <i class="ufp-icon-logout"></i>
              Logout
            </div>
          </div>
        </ng-container>
        
        <!-- Login Button Template -->
        <ng-template #loginButton>
          <button class="ufp-login-button" (click)="showLoginDialog()">
            Login / Sign Up
          </button>
        </ng-template>
      </div>
    </div>

    <!-- Date Navigation -->
    <div class="ufp-date-nav-container" [style.background-color]="currentTheme.primaryColor">
      <!-- Previous Button -->
      <button class="ufp-nav-btn prev" 
              [class.disabled]="dateScrollIndex === 0"
              (click)="scrollDates('prev')"
              [disabled]="dateScrollIndex === 0">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
      
      <!-- Date Selector -->
      <div class="ufp-dates-wrapper">
        <div class="ufp-dates" [style.transform]="'translateX(' + (-dateScrollIndex * (100 / visibleDatesCount)) + '%'">
          <div *ngFor="let date of dates; let i = index" 
               class="ufp-date" 
               [class.active]="i === selectedDateIndex"
               (click)="selectDate(i)"
               [style.background-color]="i === selectedDateIndex ? 'rgba(255, 255, 255, 0.15)' : 'transparent'">
            <div class="ufp-day" [style.color]="currentTheme.dateNavTextColor">{{date.dayName}}</div>
            <div class="ufp-date-num" [style.color]="currentTheme.dateNavTextColor">{{date.dayNumber}}</div>
            <div class="ufp-month" [style.color]="currentTheme.dateNavTextColor">{{date.monthName}}</div>
          </div>
        </div>
      </div>
      
      <!-- Next Button -->
      <button class="ufp-nav-btn next"
              [class.disabled]="dateScrollIndex + visibleDatesCount >= dates.length"
              (click)="scrollDates('next')"
              [disabled]="dateScrollIndex + visibleDatesCount >= dates.length">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
    </div>

    <!-- Selected Date Header -->
    <div class="ufp-day-header" *ngIf="dates[selectedDateIndex]">
      {{getFormattedSelectedDate()}}
    </div>

    <!-- Loading State -->
    <div class="ufp-loading" *ngIf="loading">
      <div class="ufp-loading-spinner"></div>
      <div>Loading classes...</div>
    </div>

    <!-- Error State -->
    <div class="ufp-error" *ngIf="error">
      <div class="ufp-error-icon">⚠️</div>
      <div class="ufp-error-title">Unable to Load Classes</div>
      <div class="ufp-error-description">{{ error }}</div>
      <button class="ufp-retry-button" (click)="loadClasses()">
        Try Again
      </button>
    </div>

    <!-- Empty State -->
    <div class="ufp-empty-state" *ngIf="!loading && !error && (!classes || classes.length === 0)">
      <div class="ufp-empty-icon">📅</div>
      <h3>No Classes Available</h3>
      <p>There are no classes scheduled for this date. Please try another date.</p>
    </div>

    <!-- Class List -->
    <ul class="ufp-class-list" *ngIf="!loading && !error" [style.background-color]="currentTheme.backgroundColor">
      <li *ngFor="let classItem of classes" class="ufp-class-item">
        <div class="ufp-class-time">
          {{classItem.local_start_datetime | date:'h:mm a'}}
        </div>
        <div class="ufp-class-info">
          <div class="ufp-class-name">{{classItem.type_name_primary_language}}</div>
          <div class="ufp-class-location">
            {{classItem.tenant_name_primary_language}} - {{classItem.coach_name}}
          </div>
        </div>
        <button *ngIf="allowBooking"
                class="ufp-book-button" 
                [class.full]="classItem.total_booking >= classItem.max_capacity"
                [class.booked]="isClassBooked(classItem.uuid)"
                [class.skeleton]="isLoadingStates"
                [class.subscription-required]="authService.isLoggedIn() && classItem.subscriptionStatus && !classItem.subscriptionStatus.canBook"
                [disabled]="isButtonDisabled(classItem)"
                (click)="bookClass(classItem)"
                [innerHTML]="getButtonTemplate(classItem)">
        </button>
      </li>
    </ul>
    
    <!-- Powered by Footer -->
    <div class="ufp-powered-by" (click)="openPoweredByLink($event)">
      <span>Powered by</span>
      <img src="https://d1veyxrobexfn7.cloudfront.net/widgets/ufp-logo.png" alt="UFP Logo" class="ufp-footer-logo">
      <!-- <strong class="ufp-brand-text">Unified Fitness Platform</strong> -->
      <span>by FitnessForce</span>
    </div>
  </ng-container>
</div>
